import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { Wave } from '../../types';
import authService from '../../services/authService';
import analysisService from '../../services/analysisService';
import analyticsService from '../../services/analyticsService';
import { InfoTooltip } from '../ui/InfoTooltip';

interface AnalysisPanelProps {
  waves: Wave[];
  isVisible: boolean;
  onClose: () => void;
}

interface FrequencyAnalysisResult {
  fundamentals: number[];
  harmonics: Array<{freq: number, amp: number}>;
  dominantFrequency: number;
  totalEnergy: number;
  spectralCentroid: number;
  spectralRolloff: number;
}

const analyzeFrequency = httpsCallable(functions, 'analyzeFrequency');

export const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ 
  waves, 
  isVisible, 
  onClose 
}) => {
  const [analysis, setAnalysis] = useState<FrequencyAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoAnalyze, setAutoAnalyze] = useState(true);

  useEffect(() => {
    if (isVisible && autoAnalyze && waves.length > 0 && authService.isAuthenticated()) {
      performAnalysis();
    }
  }, [waves, isVisible, autoAnalyze]);

  const performAnalysis = async () => {
    if (!authService.isAuthenticated()) {
      setError('Please sign in to perform analysis');
      analyticsService.trackError('analysis_unauthorized', 'User not authenticated');
      return;
    }

    const startTime = Date.now();
    try {
      setIsLoading(true);
      setError('');

      analyticsService.trackAnalysisStart('frequency_analysis');
      analyticsService.trackCustomDimension('analysis_waves_count', waves.length);

      const result = await analyzeFrequency({
        waveData: waves,
        sampleRate: 44100,
        duration: 1.0
      });

      const data = result.data as { success: boolean; analysis?: any };
      
      if (data.success) {
        setAnalysis(data.analysis);
        const analysisTime = Date.now() - startTime;
        analyticsService.trackPerformance('frequency_analysis', analysisTime, `waves:${waves.length}`);
        analyticsService.trackFeatureUse('analysis_success', 'frequency');
      } else {
        setError('Analysis failed');
        analyticsService.trackError('analysis_server_failed', 'Server returned failure');
      }
    } catch (error: any) {
      console.error('Analysis error:', error);
      setError(error.message || 'Analysis failed');
      analyticsService.trackError('analysis_exception', error?.message || 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  const saveAnalysis = async () => {
    if (!analysis) return;

    try {
      const name = `Frequency Analysis ${new Date().toLocaleString()}`;
      const analysisId = `analysis-${Date.now()}`;
      
      await analysisService.saveAnalysis({
        name,
        description: 'Automated frequency analysis',
        type: 'frequency_analysis',
        data: {
          waveData: waves,
          analysisResults: analysis,
          metadata: {
            sampleRate: 44100,
            duration: 1.0,
            waveCount: waves.length
          }
        },
        tags: ['frequency', 'auto-generated'],
        isPublic: false
      });

      analyticsService.trackAnalysisSave(analysisId, 'frequency_analysis');
      analyticsService.trackFeatureUse('analysis_save', 'frequency');
      
      alert('Analysis saved successfully!');
    } catch (error) {
      console.error('Save analysis error:', error);
      analyticsService.trackError('analysis_save_failed', error instanceof Error ? error.message : 'Unknown error');
      alert('Failed to save analysis');
    }
  };

  const exportAnalysis = () => {
    if (!analysis) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      waves,
      analysis,
      metadata: {
        sampleRate: 44100,
        duration: 1.0,
        waveCount: waves.length
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frequency-analysis-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-bold">Frequency Analysis</h2>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={autoAnalyze}
                onChange={(e) => setAutoAnalyze(e.target.checked)}
                className="rounded"
              />
              <span>Auto-analyze on change</span>
            </label>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </div>

        {/* Controls */}
        <div className="flex space-x-2 mb-4">
          <button
            onClick={performAnalysis}
            disabled={isLoading || waves.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Analyzing...' : 'Analyze'}
          </button>
          
          {analysis && authService.isAuthenticated() && (
            <button
              onClick={saveAnalysis}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Save Analysis
            </button>
          )}
          
          {analysis && (
            <button
              onClick={exportAnalysis}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Export JSON
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
            </div>
          )}

          {waves.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No waves to analyze. Add some waves to see analysis results.
            </div>
          )}

          {!authService.isAuthenticated() && waves.length > 0 && (
            <div className="text-center py-8 text-gray-500">
              Please sign in to perform frequency analysis.
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">Analyzing frequencies...</span>
            </div>
          )}

          {analysis && !isLoading && (
            <div className="space-y-6">
              {/* Overview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3">Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">
                      Dominant Frequency
                      <InfoTooltip text="The frequency with the highest amplitude in your waveform. This is typically the most prominent or 'loudest' pitch you'll hear." />
                    </div>
                    <div className="text-xl font-mono font-bold text-blue-600">
                      {analysis.dominantFrequency.toFixed(2)} Hz
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">
                      Total Energy
                      <InfoTooltip text="The overall power/intensity of the sound signal, calculated as the RMS (root mean square) of all frequency amplitudes. Higher values indicate louder or more energetic sounds." />
                    </div>
                    <div className="text-xl font-mono font-bold text-green-600">
                      {analysis.totalEnergy.toFixed(4)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">
                      Spectral Centroid
                      <InfoTooltip text="The 'center of mass' of the frequency spectrum. A higher value indicates a brighter sound with more high-frequency content, while a lower value indicates a darker, bass-heavy sound." />
                    </div>
                    <div className="text-xl font-mono font-bold text-purple-600">
                      {analysis.spectralCentroid.toFixed(2)} Hz
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">
                      Spectral Rolloff
                      <InfoTooltip text="The frequency below which 85% of the total energy is contained. This helps identify the bandwidth of your signal and distinguish between harmonic and noisy sounds." />
                    </div>
                    <div className="text-xl font-mono font-bold text-orange-600">
                      {analysis.spectralRolloff.toFixed(2)} Hz
                    </div>
                  </div>
                </div>
              </div>

              {/* Fundamental Frequencies */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Fundamental Frequencies</h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.fundamentals.map((freq, index) => {
                    const wave = waves[index];
                    return (
                      <div 
                        key={index}
                        className="px-3 py-2 rounded-lg text-white font-mono"
                        style={{ backgroundColor: wave?.color || '#6B7280' }}
                      >
                        {freq.toFixed(2)} Hz
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Harmonics */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Harmonics & Overtones</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {analysis.harmonics
                    .sort((a, b) => b.amp - a.amp)
                    .slice(0, 20)
                    .map((harmonic, index) => (
                      <div key={index} className="flex items-center space-x-4 py-2 px-3 bg-gray-50 rounded">
                        <div className="font-mono text-sm w-20">
                          {harmonic.freq.toFixed(1)} Hz
                        </div>
                        <div className="flex-1">
                          <div 
                            className="h-4 bg-blue-500 rounded"
                            style={{ width: `${(harmonic.amp / Math.max(...analysis.harmonics.map(h => h.amp))) * 100}%` }}
                          />
                        </div>
                        <div className="font-mono text-sm w-16 text-right">
                          {(harmonic.amp * 100).toFixed(1)}%
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Wave Configuration Summary */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Input Waves</h3>
                <div className="space-y-2">
                  {waves.filter(w => !w.muted).map((wave) => (
                    <div key={wave.id} className="flex items-center space-x-4 py-2 px-3 bg-gray-50 rounded">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: wave.color }}
                      />
                      <div className="font-mono text-sm">
                        {wave.freq} Hz
                      </div>
                      <div className="font-mono text-sm">
                        {wave.amp}% amplitude
                      </div>
                      <div className="font-mono text-sm">
                        {wave.phase}° phase
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};